import { useEffect, useState } from "react";
import { TrendingUp, Users, Activity } from "lucide-react";
import { api } from "@/lib/api";

const formatMoney = (n) => {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

const useCountUp = (target, durationMs = 1200) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(target) || target <= 0) {
      setValue(target || 0);
      return;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / durationMs, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
};

const LivePayouts = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const res = await api.get("/public/weekly-payouts");
        if (!cancelled) setStats(res.data);
      } catch {
        /* fail silently — section will simply not render */
      }
    };
    fetchStats();
    const t = setInterval(fetchStats, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const payouts = useCountUp(stats?.payouts_to_traders || 0);
  const traders = useCountUp(stats?.active_traders || 0);
  const trades = useCountUp(stats?.trades_this_week || 0);

  if (!stats) return null;

  return (
    <section
      data-testid="live-payouts"
      className="border-t border-vts-line bg-vts-bg/80"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-vts-profit/10 border border-vts-profit/30 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-vts-profit animate-pulse" />
            <span className="text-[10px] uppercase tracking-wider text-vts-profit font-mono">
              Live · all-time totals
            </span>
          </div>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl text-white tracking-tight">
            Real traders, real{" "}
            <span className="text-vts-blue">payouts</span>.
          </h2>
          <p className="text-sm lg:text-base text-vts-silver/70 mt-3 max-w-2xl mx-auto font-body">
            Updated every minute from VTS contracts settled on Deriv.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          <div
            data-testid="live-payouts-amount"
            className="vts-card p-6 lg:p-8 border-vts-blue/40"
          >
            <div className="flex items-center gap-2 text-vts-blue mb-3">
              <TrendingUp className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-wider font-mono">
                Paid to traders
              </span>
            </div>
            <div className="font-mono text-4xl lg:text-5xl text-white tabular-nums">
              {formatMoney(payouts)}
            </div>
            <div className="text-xs text-vts-silver/60 mt-2 font-mono">
              {stats.winning_trades} winning contracts
            </div>
          </div>

          <div className="vts-card p-6 lg:p-8">
            <div className="flex items-center gap-2 text-vts-silver/70 mb-3">
              <Users className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-wider font-mono">
                Traders on the desk
              </span>
            </div>
            <div
              data-testid="live-payouts-traders"
              className="font-mono text-4xl lg:text-5xl text-white tabular-nums"
            >
              {Math.round(traders)}
            </div>
            <div className="text-xs text-vts-silver/60 mt-2 font-mono">
              trading with VTS
            </div>
          </div>

          <div className="vts-card p-6 lg:p-8">
            <div className="flex items-center gap-2 text-vts-silver/70 mb-3">
              <Activity className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-wider font-mono">
                Contracts settled
              </span>
            </div>
            <div
              data-testid="live-payouts-trades"
              className="font-mono text-4xl lg:text-5xl text-white tabular-nums"
            >
              {Math.round(trades)}
            </div>
            <div className="text-xs text-vts-silver/60 mt-2 font-mono">
              settled on Deriv
            </div>
          </div>
        </div>

        <p className="text-[11px] text-vts-silver/40 mt-8 text-center font-mono">
          Real-time aggregate. No personally identifying data is shown.
        </p>
      </div>
    </section>
  );
};

export default LivePayouts;
